import { DateTime } from 'luxon';
import type { CheckInCreateRequest } from '../check-ins/schema';

// The intake's reported week, turned into the member's first check-ins. Lives
// beside the answer keys because only flow-state owners may name them: a client
// that finalizes the intake hands this the session's answers and never reads a
// day itself.

/** The seven reported-week day answers, most recent first: `day_1` is the day before the anchor. */
const REPORTED_DAY_KEYS = ['day_1', 'day_2', 'day_3', 'day_4', 'day_5', 'day_6', 'day_7'] as const;

/** The calendar date the flow dated the day pickers from, as `YYYY-MM-DD`. */
const WEEK_ANCHOR_KEY = 'week_anchor_date';

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Integers only, never rounded: the pickers emit integers, so a fractional
 * value is malformed, and rounding 0.4 to zero would invent a use-free day.
 * Zero IS a value: a recorded use-free day, not an absent answer.
 */
function isUsableDayValue(raw: unknown): raw is number {
  return typeof raw === 'number' && Number.isInteger(raw) && raw >= 0;
}

/**
 * The persisted anchor when it is a real calendar date, else `today`. A member
 * can answer the week before midnight and finalize after it; the saved dates
 * must match the picker headings they answered under.
 */
function windowAnchor(answers: Record<string, unknown>, today: string): DateTime {
  const raw = answers[WEEK_ANCHOR_KEY];
  if (typeof raw === 'string' && ISO_DAY.test(raw)) {
    const anchor = DateTime.fromISO(raw, { zone: 'utc' });
    if (anchor.isValid) return anchor;
  }
  return DateTime.fromISO(today, { zone: 'utc' });
}

/** Whether any reported-week day answer would become a check-in. */
export function hasImportableBaselineDays(answers: Record<string, unknown>): boolean {
  return REPORTED_DAY_KEYS.some((key) => isUsableDayValue(answers[key]));
}

/**
 * The reported week as dated check-in items for one program, in day order.
 * `today` (`YYYY-MM-DD`, the caller's local calendar date) is only the fallback
 * anchor. An unanswered or malformed day is skipped, never coerced to zero: an
 * unknown day is not a use-free one.
 */
export function buildBaselineCheckInItems(
  memberProgramId: string,
  answers: Record<string, unknown>,
  today: string,
): CheckInCreateRequest[] {
  const anchor = windowAnchor(answers, today);
  if (!anchor.isValid) return [];
  return REPORTED_DAY_KEYS.flatMap((key, index) => {
    const quantity = answers[key];
    if (!isUsableDayValue(quantity)) return [];
    const date = anchor.minus({ days: index + 1 }).toISODate();
    if (date === null) return [];
    return [{ date, entries: [{ memberProgramId, quantity, isFree: quantity === 0 }] }];
  });
}
